import { getWellnessLabel, getWellnessHelper, POSITIVE_EMOTIONS, CHALLENGING_EMOTIONS } from "../constants/wellness.js";
import { getInsightEmotion, isUnderStressInsight } from "./mentalPatternAnalysis.js";
import { analyzeAngerPatterns } from "./angerAnalysis.js";

/** Per-emotion contribution on a -3 (low) to +3 (high) mental-load axis (atlas keys). */
const EMOTION_WELLNESS_WEIGHT = {
  Joyful: 3,
  Loved: 3,
  Loving: 3,
  Thankful: 3,
  Hopeful: 2.5,
  Peaceful: 2.5,
  Content: 2,
  Serene: 2,
  Calm: 2,
  Focused: 2,
  Relieved: 2,
  Curious: 1,
  Excited: 1,
  Proud: 1,
  Belonging: 1.5,
  Trusting: 1.5,
  Appreciated: 1,
  Surprised: 0.5,
  Amazed: 0.5,
  Strong: 2,
  Empowered: 2,
  Confident: 1.5,
  Secure: 2,
  Safe: 2,
  Connected: 1.5,
  Accepted: 1.5,
  Optimistic: 2,
  Sad: -1,
  Angry: -1,
  Fearful: -1.5,
  Frustrated: -1.5,
  Confused: -1,
  Skeptical: -1,
  Lonely: -1.5,
  Overwhelmed: -2.5,
  Anxious: -2.5,
  Insecure: -2,
  Hostile: -2,
  Hurt: -2,
  Guilty: -1.5,
  Ashamed: -2,
  Jealous: -1.5,
  Indifferent: -0.5,
  Disgusted: -1.5,
  Embarrassed: -1,
  Stressed: -2,
  Rage: -2.5,
  Furious: -2,
  Vulnerable: -1.5,
  Isolated: -1.5,
  Depressed: -2.5,
  Miserable: -2.5,
  "Needs Support": -3,
  Other: 0
};

const RECENCY_DAYS = 14;
const RECENCY_BOOST = 2;
const MID_DAYS = 30;
const MID_BOOST = 1.25;

function parseInsightDate(insight) {
  const raw = insight.created_at != null ? insight.created_at : insight.createdAt;
  const d = raw ? new Date(raw) : new Date();
  return Number.isNaN(d.getTime()) ? new Date() : d;
}

function recencyWeight(date, now) {
  const ageDays = (now - date.getTime()) / 86400000;
  if (ageDays <= RECENCY_DAYS) return RECENCY_BOOST;
  if (ageDays <= MID_DAYS) return MID_BOOST;
  return 1;
}

function emotionWeight(emotion) {
  return EMOTION_WELLNESS_WEIGHT[emotion] ?? 0;
}

function weightedAvgToScore(sum, weight) {
  if (weight <= 0) return 50;
  const avg = sum / weight;
  const normalized = ((avg + 3) / 6) * 100;
  return Math.max(0, Math.min(100, Math.round(normalized)));
}

function getActionStats(insights) {
  let total = 0;
  let done = 0;
  (insights || []).forEach((item) => {
    const json = item.insight_json || {};
    let plan = Array.isArray(json.action_plan) ? json.action_plan : [];
    if (plan.length === 0 && (json.action || json.small_action)) {
      plan = [json.action || json.small_action];
    }
    const statuses = item.action_statuses && typeof item.action_statuses === "object" ? item.action_statuses : {};
    plan.forEach((text, index) => {
      if (!text || typeof text !== "string") return;
      total += 1;
      if (statuses[String(index)] === "done") done += 1;
    });
  });
  return { total, done };
}

function topEmotionNames(counts, filterSet, limit = 3) {
  return Object.entries(counts)
    .filter(([e, c]) => filterSet.has(e) && c > 0)
    .sort((a, b) => b[1] - a[1])
    .slice(0, limit)
    .map(([e]) => e);
}

/**
 * Wellness score 0–100 from reflection emotions, stress load, anger health, and action follow-through.
 * Returns score, label, and human-readable reasoning lines.
 */
export function analyzeWellness(insights) {
  const list = Array.isArray(insights) ? insights : [];
  const totalReflections = list.length;

  if (!totalReflections) {
    return {
      score: null,
      label: null,
      reasons: [],
      totalReflections: 0
    };
  }

  const now = Date.now();
  const reasons = [];
  let emotionSum = 0;
  let totalEmotionWeight = 0;
  let recentEmotionSum = 0;
  let recentEmotionWeight = 0;
  let stressCount = 0;
  const emotionCounts = {};

  list.forEach((item) => {
    const emotion = getInsightEmotion(item);
    const w = emotionWeight(emotion);
    const date = parseInsightDate(item);
    const rw = recencyWeight(date, now);

    emotionCounts[emotion] = (emotionCounts[emotion] || 0) + 1;
    emotionSum += w * rw;
    totalEmotionWeight += rw;

    const ageDays = (now - date.getTime()) / 86400000;
    if (ageDays <= RECENCY_DAYS) {
      recentEmotionSum += w;
      recentEmotionWeight += 1;
    }

    if (isUnderStressInsight(item)) stressCount += 1;
  });

  let score = weightedAvgToScore(emotionSum, totalEmotionWeight);

  const positiveCount = list.filter((i) => POSITIVE_EMOTIONS.has(getInsightEmotion(i))).length;
  const challengingCount = list.filter((i) => CHALLENGING_EMOTIONS.has(getInsightEmotion(i))).length;
  const positiveNames = topEmotionNames(emotionCounts, POSITIVE_EMOTIONS);
  const challengingNames = topEmotionNames(emotionCounts, CHALLENGING_EMOTIONS);

  if (positiveCount > 0) {
    reasons.push({
      id: "positive-emotions",
      tone: "positive",
      text: `${positiveCount} reflection${positiveCount !== 1 ? "s" : ""} tagged with lower-load emotions${positiveNames.length ? ` (${positiveNames.join(", ")})` : ""}.`,
      impact: Math.min(12, positiveCount * 2)
    });
  }

  if (challengingCount > 0) {
    const pct = Math.round((challengingCount / totalReflections) * 100);
    reasons.push({
      id: "challenging-emotions",
      tone: "negative",
      text: `${challengingCount} reflection${challengingCount !== 1 ? "s" : ""} (${pct}%) show high-intensity emotions${challengingNames.length ? ` such as ${challengingNames.join(", ")}` : ""}.`,
      impact: -Math.min(15, Math.round(pct * 0.2))
    });
  }

  if (stressCount > 0) {
    const stressPct = Math.round((stressCount / totalReflections) * 100);
    const stressPenalty = Math.min(18, Math.round(stressPct * 0.22));
    score -= stressPenalty;
    reasons.push({
      id: "under-stress",
      tone: "negative",
      text: `${stressCount} reflection${stressCount !== 1 ? "s" : ""} (${stressPct}%) read as under stress — overwhelm, anxiety, pressure, or similar.`,
      impact: -stressPenalty
    });
  }

  const anger = analyzeAngerPatterns(list);
  if (anger.totalAnger > 0 && anger.healthyAngerScore != null) {
    const angerAdj = Math.round((anger.healthyAngerScore - 50) * 0.12);
    score += angerAdj;
    reasons.push({
      id: "anger-health",
      tone: angerAdj >= 0 ? "positive" : "negative",
      text: `${anger.totalAnger} anger-related reflection${anger.totalAnger !== 1 ? "s" : ""}: ${anger.healingCount} deliberate (recognition) vs ${anger.survivalCount} reactive (control). Anger pattern score ${anger.healthyAngerScore}/100.`,
      impact: angerAdj
    });
  }

  const actions = getActionStats(list);
  if (actions.total > 0) {
    const donePct = Math.round((actions.done / actions.total) * 100);
    const actionBonus = Math.min(8, Math.round(donePct * 0.08));
    if (actionBonus > 0) {
      score += actionBonus;
      reasons.push({
        id: "actions-done",
        tone: "positive",
        text: `You completed ${actions.done} of ${actions.total} action steps from reflections (${donePct}%).`,
        impact: actionBonus
      });
    } else if (actions.total >= 2) {
      reasons.push({
        id: "actions-pending",
        tone: "neutral",
        text: `${actions.total} action steps from reflections are still open — closing loops can reduce mental load.`,
        impact: 0
      });
    }
  }

  if (recentEmotionWeight >= 2) {
    const recentScore = weightedAvgToScore(recentEmotionSum, recentEmotionWeight);
    const trendAdj = Math.round((recentScore - score) * 0.15);
    if (Math.abs(trendAdj) >= 2) {
      score += trendAdj;
      reasons.push({
        id: "recent-trend",
        tone: trendAdj >= 0 ? "positive" : "negative",
        text:
          trendAdj >= 0
            ? `Your last 2 weeks show slightly lower load than your overall average.`
            : `Your last 2 weeks show slightly higher load than your overall average.`,
        impact: trendAdj
      });
    }
  }

  const needsSupport = emotionCounts["Needs Support"] || 0;
  if (needsSupport > 0) {
    const crisisPenalty = Math.min(25, needsSupport * 12);
    score -= crisisPenalty;
    reasons.push({
      id: "needs-support",
      tone: "negative",
      text: `${needsSupport} reflection${needsSupport !== 1 ? "s" : ""} flagged as needing support — this weighs heavily on your mental load index.`,
      impact: -crisisPenalty
    });
  }

  score = Math.max(0, Math.min(100, Math.round(score)));

  reasons.sort((a, b) => Math.abs(b.impact) - Math.abs(a.impact));

  const summaryReason = {
    id: "summary",
    tone: "neutral",
    text: `Index blends emotion tags across ${totalReflections} reflection${totalReflections !== 1 ? "s" : ""}, with extra weight on the last 2 weeks. Under-stress load, anger pattern mix, and completed responses also adjust the result.`,
    impact: 0
  };

  return {
    score,
    label: getWellnessLabel(score),
    helper: getWellnessHelper(score),
    reasons: [summaryReason, ...reasons],
    totalReflections,
    positiveCount,
    stressCount,
    angerHealthyScore: anger.healthyAngerScore
  };
}
